import { ICategory, ICropByCategory, IPreferenceType } from '@ninjacart/nc-nk-core';
import {
  addEventParam,
  INinjaEvent,
  useQueuedEventStore
} from '@ninjacart/nc-events-sdk-v2';
import {
  createUserPreferenceApi,
  fetchCropMediaDetailApi,
  getCategoryData,
  getCropsByCategories,
  getUserPreferenceDetail,
  IUserCropPreference,
  KissanEventType,
  updateUserPreferenceApi,
  useAppPersistenceStore,
  useCropPreferenceStore,
  useGlobalLoginContext,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import { Button, CropsHomePlaceHolder, Text, useTheme, View, XStack, YStack } from '@ninjacart/nc-nk-ui';
import { useFocusEffect, useRouter } from '@ninjacart/nc-nk-web-deps';
import { Plus } from '@tamagui/lucide-icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native';
import { CropCard } from './cropTypeCard';

export const CropsSelectionCard = ({ sourcePage }: { sourcePage: string }) => {
  const { t } = useTranslation();
  const setCropPreference = useCropPreferenceStore((state) => state.setCropPreference);
  const setUserPreferenceId = useCropPreferenceStore((state) => state.setUserPreferenceId);
  const userPreferenceId = useCropPreferenceStore((state) => state.userPreferenceId);
  const cropPreference = useCropPreferenceStore((state) => state.cropPreference);
  const [selectedCrops, setSelectedCrops] = useState<
    Array<{
      name: string;
      domainId: String;
      type: string;
      isSubjectMatterExpert: boolean;
      priority: string;
    }>
  >([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [crops, setCrops] = useState<ICropByCategory[]>([]);
  const userProfile = useProfileStore((state) => state.userProfile);
  const [isLoading, setIsLoading] = useState(false);
  const { push } = useRouter();
  const theme = useTheme();
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const selectedLanguage = useAppPersistenceStore((state) => state.appLanguage);
  const setCropLangaugeByName = useCropPreferenceStore((state) => state.setCropLangaugeByName);
  const uniqueDeviceId = useAppPersistenceStore((state) => state.deviceId);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const fields = useGlobalLoginContext(['openLoginSheet', 'previousPage','navigationParams']);
  const setNavigationParams = fields.navigationParams.set;

  useFocusEffect(
    useCallback(() => {
      if (hasUserLoggedIn()) {
        userCropPreferenceDetail();
      } else {
        initializeCropCategory();
      }
    }, [])
  );

  const userCropPreferenceDetail = async () => {
    setIsLoading(true);
    const response: IUserCropPreference | undefined = await getUserPreferenceDetail(
      IPreferenceType?.CROP
    );
    if (!response?.data?.cropPreferences?.length) {
      initializeCropCategory();
    } else {
      prefillCropPreference(response?.data?.cropPreferences);
    }
  };

  const initializeCropCategory = async () => {
    try {
      const response: any = await getCategoryData();
      setCategories(response?.data);
      if (response?.data[0]?.id) {
        cropsByCategories(response?.data[0]?.id);
      }
    } catch (error) {
      console.error('Error fetching crop categories:', error);
    }
  };

  const cropsByCategories = async (categoryId: number) => {
    try {
      const response: any = await getCropsByCategories(categoryId);
      setCrops(response?.data);
    } catch (error) {
      console.error('Error fetching crops by category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const prefillCropPreference = async (preference) => {
    try {
      const cropIds = preference?.map((crop) => crop?.domainId?.toString().trim()).filter(Boolean);

      const mediaDetails: any = await fetchCropMediaDetailApi(cropIds);
      if (mediaDetails?.data?.length) {
        const cropsWithMedia: ICropByCategory[] = await mediaDetails?.data?.map((mediaDetail) => ({
          ...mediaDetail,
        }));

        const languageByCropNames = cropsWithMedia?.map(
          (crop) =>
            crop?.cropDetail?.find((detail) => detail?.language === selectedLanguage)?.name ||
            crop?.name
        );
        setCropLangaugeByName(languageByCropNames || []);
        const formattedCrops = await cropsWithMedia?.map((crop) => ({
          name: crop?.name,
          domainId: crop?.id,
          type: 'Crop',
          isSubjectMatterExpert: true,
          priority: 'Primary',
        }));
        setCrops(cropsWithMedia);
        setSelectedCrops(formattedCrops);
      }
    } catch (e) {
      console.error('Error fetching crop Detail Api:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectedCropIdEvent = (item) => {
    const additionalDetails = [
      addEventParam('deviceId', uniqueDeviceId),
      addEventParam('cropId', item?.id),
      addEventParam('cropName', item?.name),
    ];
    const selectedCropBeforeLoginEvent: INinjaEvent = {
      name: KissanEventType.SELECTED_CROP_BEFORE_LOGIN,
      screenDetails: { screen: 'crop page' },
      eventParams: additionalDetails,
    };
    enqueue(selectedCropBeforeLoginEvent);
  };

  const toggleSelectItem = (item) => {
    if (!hasUserLoggedIn()) {
      handleSelectedCropIdEvent(item);
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set('FEED PAGE');
      setNavigationParams({
        name: '',
        routeName: { name: `crops/index`, params: { sourcePage: sourcePage } },
        description: `${t('page.addOnCrops')}`,
        screenName: 'FEED PAGE',
      });
      return;
    }
    const itemIndex = selectedCrops.findIndex((selectedItem) => selectedItem?.name === item?.name);

    let updatedSelectedCrops;

    if (itemIndex > -1) {
      handleCropCategoryToggleEvent(item?.name, KissanEventType.CROP_REMOVE);
      updatedSelectedCrops = selectedCrops.filter((_, index) => index !== itemIndex);
    } else {
      handleCropCategoryToggleEvent(item?.name, KissanEventType.CROP_CATEGORY_SELECT);
      updatedSelectedCrops = [
        ...(selectedCrops || []),
        {
          name: item?.name,
          domainId: item?.id,
          type: 'Crop',
          isSubjectMatterExpert: true,
          priority: 'Primary',
        },
      ];
    }
    setSelectedCrops(updatedSelectedCrops);
  };

  const navigateToCropsListPage = () => {
    if (!hasUserLoggedIn()) {
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set('FEED PAGE');
      setNavigationParams({
        name: '',
        routeName: { name: `crops/index`, params: { sourcePage: sourcePage } },
        description: `${t('page.addOnCrops')}`,
        screenName: 'FEED PAGE',
      });
    } else {
      push({
        pathname: `/crops`,
        query: {
          sourcePage: sourcePage,
        },
      });
    }
  };

  const handlePreferenceChange = async () => {
    setIsLoading(true);
    try {
      const cropPreferencesData = {
        cropPreferences: [...selectedCrops],
      };

      const inputWithExistingPreference = {
        input: {
          id: userPreferenceId,
          node: 'cropPreferences',
          data: cropPreferencesData,
        },
      };

      const inputWithNewPreference = {
        input: {
          UserPreference: {
            realmId: userProfile?.realm?.id,
            userId: Number(userProfile?.user?.id),
            ...cropPreferencesData,
          },
        },
      };

      const config: any = {
        realmId: userProfile?.realm?.id,
        userId: userProfile?.user?.id,
        formData: cropPreference?.length > 0 ? inputWithExistingPreference : inputWithNewPreference,
      };

      let response: any;

      if (cropPreference?.length > 0) {
        response = await updateUserPreferenceApi(config);
      } else {
        response = await createUserPreferenceApi(config);
      }
      if (response?.data?.success) {
        const preferenceData =
          cropPreference?.length > 0
            ? response?.data?.UpdatedUserPreferenceData?.UserPreference
            : response?.data?.CreatedUserData?.UserPreference;

        setCropPreference(preferenceData?.cropPreferences);
        setUserPreferenceId(preferenceData?.id);
        prefillCropPreference(preferenceData?.cropPreferences);
        handleCropCategoryToggleEvent(preferenceData?.cropPreferences, KissanEventType.CROP_ADDED);
      }
    } catch (error) {
      console.error('error save crop preference', error);
    }
  };

  const handleCropCategoryToggleEvent = (selectedCrop, eventAction) => {
    const cropData = Array.isArray(selectedCrop)
      ? selectedCrop?.map((crop) => crop?.name)
      : selectedCrop;

    const sendEventsToQueue = {
      name: eventAction,
      screenDetails: { screen: 'crops list page' },
      eventParams: [addEventParam('selectedCategory', cropData)],
    };
    enqueue(sendEventsToQueue);
  };

  const displayedCrops = useMemo(() => {
    return crops?.length > 0 ? crops.slice(0, Math.min(crops?.length, 3)) : [];
  }, [crops]);

  return isLoading ? (
    <CropsHomePlaceHolder />
  ) : (
    <ScrollView>
      <View paddingHorizontal={14} paddingTop={14} marginBottom={8} minHeight={120}>
        <YStack>
          <Text fontSize={12} fontWeight={'400'} marginBottom={6} color={'$grey'}>
            {t('page.cropsYouGrow')}
          </Text>
          <Text fontSize={14} fontWeight={'500'} color="$color">
            {t('page.helpCropInformations')}
          </Text>
        </YStack>

        <XStack flexDirection="row" justifyContent={'flex-start'} marginTop="$4" width={'100%'}>
          <YStack
            alignItems="center"
            padding={8}
            borderStyle="dashed"
            borderColor="$grey"
            borderRadius={7}
            width={'23%'}
            height={110}
            backgroundColor={'$cardbackground'}
            onPress={navigateToCropsListPage}
            style={{ position: 'relative' }}
            borderWidth={0.89}
            margin={4}
            testID='kisan_tId_addCrop'
          >
            <Text
              style={{
                paddingVertical: 20,
                textAlign: 'center',
              }}
              fontSize={16}
              fontWeight="400"
              color="$grey"
            >
              {t('page.addCrops')}
            </Text>

            <YStack
              position="absolute"
              top={4}
              right={5}
              alignItems="center"
              justifyContent="center"
              borderWidth={0.89}
              borderColor="$primary"
              borderRadius={10}
              borderStyle="dotted"
            >
              <Plus color="$primary" size={12} />
            </YStack>
          </YStack>
          {displayedCrops?.map((crop, index) => {
            const mediaElement = Array.isArray(crop?.media)
              ? crop?.media.find((media) => media?.type === 'CropIconImage')
              : crop?.media;
            const regionalName = crop?.cropDetail?.find(
              (element) => element?.language === selectedLanguage
            )?.name;

            return (
              <CropCard
                key={crop?.id || index}
                crop={crop}
                isSelected={selectedCrops.some((selectedItem) => selectedItem?.name === crop?.name)}
                onSelect={() => !cropPreference?.length && toggleSelectItem(crop)}
                mediaUrl={mediaElement?.mediaUrl}
                regionalName={regionalName}
                type={IPreferenceType.CROP}
              />
            );
          })}
        </XStack>

        {selectedCrops?.length > 0 && !cropPreference?.length && (
          <XStack justifyContent="center" paddingTop={12} alignItems="center">
            <Button
              width={140}
              backgroundColor="$primary"
              paddingHorizontal="$4"
              borderRadius="$6"
              onPress={handlePreferenceChange}
            >
              <View>
                <Text color="$light" fontWeight="500">
                  {' '}
                  {t('page.save')}
                </Text>
              </View>
            </Button>
          </XStack>
        )}
      </View>
    </ScrollView>
  );
};
